import axios from "axios";
import { getMetaFbp, getMetaFbc } from "./meta";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: { "Content-Type": "application/json" },
});

// Attach access token + Meta cookies to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("spp_access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const fbp = getMetaFbp();
  const fbc = getMetaFbc();
  if (fbp) config.headers["X-FBP"] = fbp;
  if (fbc) config.headers["X-FBC"] = fbc;
  return config;
});

// Safe internal-only redirect helper. Only accepts our own routes so we
// never round-trip an arbitrary externally-controlled URL through returnTo.
const SAFE_INTERNAL_PATHS = ["/dashboard", "/admin"];
const buildSignInRedirect = () => {
  const currentPath = window.location.pathname || "/";
  if (SAFE_INTERNAL_PATHS.includes(currentPath)) {
    return `/?signin=1&returnTo=${encodeURIComponent(currentPath)}`;
  }
  return "/?signin=1";
};

// Auto-refresh on 401
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem("spp_refresh_token");
      if (!refreshToken) {
        isRefreshing = false;
        localStorage.clear();
        window.location.href = buildSignInRedirect();
        return Promise.reject(error);
      }

      try {
        const res = await axios.post(`${BACKEND_URL}/api/auth/refresh`, {
          refresh_token: refreshToken,
        });
        const { access_token, refresh_token: newRefresh } = res.data;
        localStorage.setItem("spp_access_token", access_token);
        localStorage.setItem("spp_refresh_token", newRefresh);
        processQueue(null, access_token);
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.clear();
        window.location.href = buildSignInRedirect();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  firebaseVerify: (id_token, first_name, mode = "free_sample") =>
    api.post("/auth/firebase-verify", { id_token, first_name, mode }),
  logOtpRequest: (phone, first_name) => api.post("/auth/log-otp-request", { phone, first_name }),
  validateWaToken: (wa_token) => api.post("/wa-links/validate", { wa_token }),
  trackWaEvent: (wa_token, event_name, metadata) => api.post("/wa-links/track-event", { wa_token, event_name, metadata }),
  getMe: () => api.get("/auth/me"),
  logout: (refreshToken) => api.post("/auth/logout", { refresh_token: refreshToken }),
};

// User Preferences API
export const userAPI = {
  savePreferences: (preferred_categories) =>
    api.put("/auth/me/preferences", { preferred_categories }),
};

// Images API
export const imagesAPI = {
  list: (params) => api.get("/images", { params }),
  download: (publicId) => api.get(`/images/download/${encodeURIComponent(publicId)}`),
  uploadCSV: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/admin/csv/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
};

// Announcements API
export const announcementsAPI = {
  list: () => api.get("/announcements"),
};

// Downloads API
export const downloadsAPI = {
  history: (params) => api.get("/downloads/history", { params }),
};

// Device fingerprint generation
export const generateDeviceFingerprint = () => {
  const data = {
    userAgent: navigator.userAgent,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language || navigator.userLanguage,
  };
  // Simple hash for fingerprint
  const str = JSON.stringify(data);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(16).padStart(8, '0');
};

// Enhanced download with fingerprint headers
export const downloadWithFingerprint = (publicId, previewOpened = false) => {
  const fingerprint = generateDeviceFingerprint();
  return api.get(`/images/download/${encodeURIComponent(publicId)}`, {
    headers: {
      "X-Device-Fingerprint": fingerprint,
      "X-Screen-Resolution": `${window.screen.width}x${window.screen.height}`,
      "X-Timezone": Intl.DateTimeFormat().resolvedOptions().timeZone,
      "X-Language": navigator.language || navigator.userLanguage,
      "X-Preview-Opened": previewOpened ? "true" : "false",
    }
  });
};

// Payment API
export const paymentAPI = {
  createOrder: (planId, couponCode) => api.post("/payment/create-order", { plan_id: planId, coupon_code: couponCode }),
  verify: (data) => api.post("/payment/verify", data),
  validateCoupon: (planId, couponCode) => api.post("/payment/validate-coupon", { plan_id: planId, coupon_code: couponCode }),
};

// Coupon API
export const couponAPI = {
  validate: (couponCode) => api.post("/coupons/validate", { coupon_code: couponCode }),
};

// Admin API
export const adminAPI = {
  metrics: () => api.get("/admin/metrics"),
  analytics: () => api.get("/admin/analytics"),
  listUsers: (params) => api.get("/admin/users", { params }),
  createUser: (data, pin) => api.post("/admin/users", data, { headers: { "X-Admin-PIN": pin } }),
  updateUser: (userId, data, pin) => api.put(`/admin/users/${userId}`, data, { headers: { "X-Admin-PIN": pin } }),
  exportUsers: (format, plan, search) => {
    const params = new URLSearchParams();
    params.append("format", format);
    if (plan) params.append("plan", plan);
    if (search) params.append("search", search);
    return api.get(`/admin/users/export?${params.toString()}`, { responseType: "blob" });
  },
  listImages: (params) => api.get("/admin/images", { params }),
  deleteImage: (publicId, pin) => api.delete(`/admin/images/${encodeURIComponent(publicId)}`, { headers: { "X-Admin-PIN": pin } }),
  listAnnouncements: () => api.get("/announcements"),
  createAnnouncement: (data) => api.post("/admin/announcements", data),
  updateAnnouncement: (id, data) => api.put(`/admin/announcements/${id}`, data),
  deleteAnnouncement: (id) => api.delete(`/admin/announcements/${id}`),
  // Coupon management
  listCoupons: (params) => api.get("/admin/coupons", { params }),
  createCoupon: (data, pin) => api.post("/admin/coupons", data, { headers: { "X-Admin-PIN": pin } }),
  getCoupon: (id) => api.get(`/admin/coupons/${id}`),
  updateCoupon: (id, data, pin) => api.put(`/admin/coupons/${id}`, data, { headers: { "X-Admin-PIN": pin } }),
  deleteCoupon: (id, pin) => api.delete(`/admin/coupons/${id}`, { headers: { "X-Admin-PIN": pin } }),
  getCouponStats: (id) => api.get(`/admin/coupons/${id}/stats`),
  // Security management
  securitySummary: () => api.get("/admin/security/summary"),
  securityFlags: (params) => api.get("/admin/security/flags", { params }),
  resolveFlag: (userId) => api.post(`/admin/security/flags/${userId}/resolve`),
  flagDetails: (userId) => api.get(`/admin/security/flags/${userId}/details`),
  // Product Report
  productReport: (params) => api.get("/admin/product-report", { params }),
  exportProductReport: (type, format, dateFilter) => {
    const params = new URLSearchParams();
    params.append("format", format);
    params.append("date_filter", dateFilter);
    return api.get(`/admin/product-report/export/${type}?${params.toString()}`, { responseType: "blob" });
  },
  // OTP Dropoffs
  otpDropoffs: (params) => api.get("/admin/otp-dropoffs", { params }),
  // WA Analytics
  waAnalytics: () => api.get("/wa-links/analytics"),
  waTestSendPreview: (data) => api.post("/wa-links/test-send-preview", data),
  waTestSend: (data) => api.post("/wa-links/test-send", data),
  waFlowPreview: (data) => api.post("/wa-links/flow-trigger-preview", data),
  waFlowTrigger: (data) => api.post("/wa-links/flow-trigger", data),
  waWebhookGet: () => api.get("/wa-links/settings/webhook"),
  waWebhookUpdate: (url) => api.put("/wa-links/settings/webhook", { url }),
  // WA Cron
  waCronStatus: () => api.get("/wa-links/cron/status"),
  waCronToggle: (enabled, pin) => api.put("/wa-links/cron/toggle", { enabled }, { headers: { "X-Admin-Pin": pin } }),
  waCronBackfill: (pin) => api.post("/wa-links/cron/backfill", {}, { headers: { "X-Admin-Pin": pin } }),
  waCronRunNow: (dryRun, pin) => api.post(`/wa-links/cron/run-now?dry_run=${dryRun}`, {}, { headers: { "X-Admin-Pin": pin } }),
  // Internal analytics funnel
  analyticsV2Funnel: (days = 30) => api.get(`/analytics-events/funnel?days=${days}`),
  analyticsV2Collections: () => api.get("/analytics-events/collections"),
  analyticsV2Backfill: () => api.post("/analytics-events/backfill/purchases"),
};

export default api;
